import {useCallback, useRef, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {URL_PARAMS, SECONDARY_PARAMS} from './urlParams'

/**
 * Custom hook for managing tab state synchronized with URL
 * Supports both simple tabs and nested tabs (subtabs)
 *
 * @param defaultTab - Default tab ID when no tab is in URL
 * @param paramName - URL parameter name (default: URL_PARAMS.TAB). Use different names for nested tabs.
 * @param clearSubtabs - Whether to clear subtab params when this tab changes (default: true for main tab, false for others)
 *
 * @example
 * // Simple tab usage
 * const {activeTab, setActiveTab} = useUrlTab('overview')
 *
 * // Nested tabs - parent level (clears subtabs on change)
 * const {activeTab, setActiveTab} = useUrlTab('code-repo')
 * // URL: /team/123?tab=code-repo
 *
 * // Nested tabs - child level (use different param name)
 * const {activeTab: activeSubTab, setActiveTab: setActiveSubTab} = useUrlTab('commits', URL_PARAMS.SUBTAB)
 * // URL: /team/123?tab=code-repo&subtab=commits
 */
export const useUrlTab = (defaultTab: string, paramName: string = URL_PARAMS.TAB, clearSubtabs?: boolean) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isInitialMount = useRef(true)

  // By default, clear subtabs only when paramName is main tab
  const shouldClearSubtabs = clearSubtabs ?? paramName === URL_PARAMS.TAB

  // Get current tab from URL or use default
  const urlTab = searchParams.get(paramName)
  const activeTab = urlTab || defaultTab

  // Set default tab in URL on initial mount only
  // Note: We don't clear secondary params on initial mount to preserve pagination/subtabs on refresh
  useEffect(() => {
    if (isInitialMount.current && !urlTab) {
      setSearchParams(
        params => {
          const newParams = new URLSearchParams(params)
          newParams.set(paramName, defaultTab)
          // Don't clear secondary params on initial mount - only on user-initiated tab changes
          return newParams
        },
        {replace: true},
      )
    }
    isInitialMount.current = false
  }, [])

  // Update URL when tab changes
  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        params => {
          const newParams = new URLSearchParams(params)
          newParams.set(paramName, tab)

          // Clear secondary params when main tab changes
          if (shouldClearSubtabs) {
            SECONDARY_PARAMS.forEach(param => {
              if (param !== paramName) {
                newParams.delete(param)
              }
            })
          }

          return newParams
        },
        {replace: true},
      )
    },
    [setSearchParams, paramName, shouldClearSubtabs],
  )

  return {
    activeTab,
    setActiveTab,
  }
}
